import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { BedDouble, UtensilsCrossed, Camera, Bus, UserCheck, Drama, Plane, TrainFront, MessageCircle, Check } from "lucide-react";
import { PRICING, ITINERARY, KEY_ATTRACTIONS, ROUTE_CHIPS, WHATSAPP_URL } from "../data/tour";
import { openEnquire } from "./EnquireModal";
import { Reveal, SectionHead } from "./Reveal";

const CHIP_ICONS = {
  Stay: BedDouble,
  "Breakfast + Dinner": UtensilsCrossed,
  Sightseeing: Camera,
  "Local Transport": Bus,
  "Trip Captain": UserCheck,
  "Cultural Shows": Drama,
  Flights: Plane,
  Trains: TrainFront,
};

const nextWeeklyDates = (weekday, count = 5) => {
  const out = [];
  const d = new Date();
  d.setDate(d.getDate() + (((weekday - d.getDay()) + 7) % 7 || 7));
  while (out.length < count) {
    out.push(new Date(d));
    d.setDate(d.getDate() + 7);
  }
  return out;
};

const fmtDate = (d) => d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });

const batchDates = (batches) =>
  Array.isArray(batches) ? batches : nextWeeklyDates(batches === "fridays" ? 5 : 0).map(fmtDate);

const PackageCard = ({ pkg, index }) => (
  <Reveal delay={index * 0.08}>
    <article
      data-testid={`pricing-card-${pkg.id}`}
      className="overflow-hidden rounded-2xl border border-vine bg-moss transition-shadow duration-500 hover:shadow-[0_20px_60px_rgba(27,42,23,0.12)]"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <div className="relative lg:col-span-4">
          <img
            src={pkg.image}
            alt={pkg.title}
            data-testid={`pricing-image-${pkg.id}`}
            className="h-60 w-full object-cover lg:h-full"
          />
          <span className="absolute left-4 top-4 rounded-full bg-marigold px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-jungle" data-testid={`pricing-badge-${pkg.id}`}>
            {pkg.badge}
          </span>
        </div>

        <div className="p-7 sm:p-9 lg:col-span-8">
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.2em]">
            <span className="rounded-full border border-vine px-3 py-1 text-sage" data-testid={`pricing-duration-${pkg.id}`}>{pkg.duration}</span>
            <span className="text-marigold" data-testid={`pricing-schedule-${pkg.id}`}>{pkg.schedule}</span>
          </div>

          <h3 className="mt-4 font-serif text-2xl font-medium leading-tight tracking-tight text-cream sm:text-3xl">
            {pkg.title}
          </h3>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-sage">{ROUTE_CHIPS.join("  ·  ")}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {pkg.chips.map((chip) => {
              const Icon = CHIP_ICONS[chip] || Check;
              return (
                <span key={chip} className="flex items-center gap-1.5 rounded-full border border-vine px-3 py-1.5 text-[11px] font-medium text-sage">
                  <Icon size={13} className="text-marigold" />
                  {chip}
                </span>
              );
            })}
          </div>

          <div className="mt-5" data-testid={`pricing-batches-${pkg.id}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-sage">Upcoming Batches</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {batchDates(pkg.batches).map((d) => (
                <span key={d} className="rounded-full border border-marigold/40 bg-jungle px-3.5 py-1.5 text-[11px] font-semibold text-cream">
                  {d}
                </span>
              ))}
            </div>
          </div>

          <Accordion type="single" collapsible className="mt-6 w-full" data-testid={`pricing-accordion-${pkg.id}`}>
            <AccordionItem value="itinerary" className="border-vine">
              <AccordionTrigger data-testid={`pricing-itinerary-toggle-${pkg.id}`} className="text-sm font-bold uppercase tracking-[0.15em] text-cream hover:text-marigold hover:no-underline">
                Brief Itinerary
              </AccordionTrigger>
              <AccordionContent>
                {ITINERARY.map((d) => (
                  <div key={d.day} className="flex gap-4 border-b border-vine py-2.5 last:border-0">
                    <span className="w-12 shrink-0 text-[11px] font-bold uppercase tracking-[0.15em] text-marigold">Day {Number(d.day)}</span>
                    <p className="text-sm leading-relaxed text-sage">
                      <span className="font-semibold text-cream/90">{d.route}</span> — {d.title}
                    </p>
                  </div>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="attractions" className="border-vine">
              <AccordionTrigger data-testid={`pricing-attractions-toggle-${pkg.id}`} className="text-sm font-bold uppercase tracking-[0.15em] text-cream hover:text-marigold hover:no-underline">
                Key Attractions
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2.5">
                  {KEY_ATTRACTIONS.map((a) => (
                    <li key={a} className="flex items-start gap-2.5 text-sm leading-relaxed text-sage">
                      <Check size={14} className="mt-1 shrink-0 text-marigold" />
                      {a}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="mt-7 flex flex-wrap items-end justify-between gap-5 border-t border-vine pt-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-sage">Starts From</p>
              <p className="mt-1 font-serif text-4xl font-medium tracking-tight text-cream" data-testid={`pricing-price-${pkg.id}`}>
                {pkg.price}
                <span className="ml-2 font-sans text-xs font-semibold uppercase tracking-[0.15em] text-sage">{pkg.suffix} · Per Person</span>
              </p>
              <p className="mt-2 text-xs leading-relaxed text-sage">{pkg.note}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => openEnquire(pkg.id)}
                data-testid={`pricing-cta-${pkg.id}`}
                className="rounded-full bg-marigold px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] text-jungle transition-colors duration-300 hover:bg-marigold-hover"
              >
                Enquire Now
              </button>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp about this package"
                data-testid={`pricing-whatsapp-${pkg.id}`}
                className="flex h-12 w-12 items-center justify-center rounded-full border border-vine text-sage transition-colors duration-300 hover:border-marigold hover:text-marigold"
              >
                <MessageCircle size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </article>
  </Reveal>
);

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="border-y border-vine bg-moss/50 py-28">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <SectionHead index="01" overline="Kerala Packages — Trending Now" title="Pick your way down south." />
      <div className="mt-16 space-y-8">
        {PRICING.map((pkg, i) => (
          <PackageCard key={pkg.id} pkg={pkg} index={i} />
        ))}
      </div>
      <Reveal delay={0.2} className="mt-10">
        <p className="text-xs leading-relaxed text-sage" data-testid="pricing-note">
          Flight seats are limited per batch and priced at booking. Train quotas open ~60 days out —
          reserve early. All packages include the full 6N/7D Kochi-to-Kochi itinerary.
        </p>
      </Reveal>
    </div>
  </section>
);
